import { RefreshCwIcon, TriangleAlertIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { errorMessage, errorTitle, fieldViolations } from "@/lib/errors";
import { cn } from "@/lib/utils";

interface ErrorStateProps {
  /** Anything throwable; ConnectError details are unpacked when present. */
  error: unknown;
  /** Heading override; otherwise derived from the error code. */
  title?: string;
  /** Rendered as a retry affordance when supplied. */
  onRetry?: () => void;
  className?: string;
}

/**
 * The single place a failed read is rendered.
 *
 * Screens must never fall back to an empty table on error: an operator looking
 * at "no accounts" when the API is down will make the wrong decision.
 */
export function ErrorState({ error, title, onRetry, className }: ErrorStateProps) {
  const violations = fieldViolations(error);

  return (
    <Alert variant="destructive" className={cn("border-tone-danger/40 bg-tone-danger/5", className)}>
      <TriangleAlertIcon />
      <AlertTitle>{title ?? errorTitle(error, "Could not load this data")}</AlertTitle>
      <AlertDescription>
        <p>{errorMessage(error, "The service did not answer.")}</p>
        {violations.length > 0 ? (
          <ul className="mt-1 list-outside list-disc space-y-0.5 pl-4">
            {violations.map((v, i) => (
              <li key={`${v.field}-${i}`}>
                <code className="font-mono text-xs">{v.field}</code> — {v.description}
              </li>
            ))}
          </ul>
        ) : null}
        {onRetry ? (
          <Button variant="outline" size="sm" className="mt-2" onClick={onRetry}>
            <RefreshCwIcon /> Try again
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/** Inline, single-line variant for a form control. */
export function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return <p className="text-xs text-destructive">{message}</p>;
}
